import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, MessageSquare, Save } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, selectClass, textareaClass } from "../components/FormField";
import {
  commentAuditorDispute,
  commentHospitalDispute,
  fetchAuditorDispute,
  fetchHospitalDispute,
  updateAuditorDisputeStatus,
  updateHospitalDisputeStatus,
  type DisputeApi,
  type DisputeStatusApi,
} from "../api/disputeApi";
import { getAccountTypeRoute, getAuthToken, getStoredAccountType } from "../auth";
import { allDisputesList, hospitalDisputes } from "../data";
import type { DisputeStatus } from "../types";
import { cn } from "@/shared/lib/cn";

function formatAmount(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

const statusOptions: { value: DisputeStatusApi; label: string }[] = [
  { value: "inProgress", label: "En cours" },
  { value: "resolved", label: "Traité" },
  { value: "rejected", label: "Rejeté" },
  { value: "closed", label: "Clôturé" },
];

const statusBadgeClass: Record<DisputeStatus, string> = {
  inProgress: "bg-accent-soft text-accent-foreground",
  resolved: "bg-accent/15 text-accent",
  rejected: "bg-danger-soft text-danger",
  closed: "bg-card-muted text-muted-foreground",
};

function mockDispute(id: string, isHospital: boolean): DisputeApi | null {
  const list = isHospital
    ? hospitalDisputes.map((d) => ({
        id: Number.parseInt(d.id.replace(/\D/g, ""), 10) || 0,
        display_id: Number.parseInt(d.id.replace(/\D/g, ""), 10) || 0,
        reference: d.reference,
        invoice: d.invoice,
        invoice_id: 0,
        hospital_id: 0,
        hospital: "",
        company_id: 0,
        company: d.company,
        amount: 0,
        status: d.status as DisputeStatusApi,
        submitted_date: d.createdAt,
        createdAt: d.createdAt,
        description: "Description mock du litige.",
        comments: [],
      }))
    : allDisputesList.map((d) => ({
        id: Number(d.id) || d.displayId,
        display_id: d.displayId,
        reference: d.reference,
        invoice: d.invoice,
        invoice_id: 0,
        hospital_id: 0,
        hospital: d.hospital,
        company_id: 0,
        company: d.company,
        amount: d.amount,
        status: d.status as DisputeStatusApi,
        submitted_date: d.submittedDate,
        createdAt: d.createdAt,
        description: "Description mock du litige.",
        comments: [],
      }));

  return list.find((d) => String(d.id) === id || String(d.display_id) === id) ?? null;
}

type DisputeDetailPageProps = {
  disputeId?: string;
};

export function DisputeDetailPage({ disputeId }: DisputeDetailPageProps = {}) {
  const params = useParams();
  const id = disputeId ?? params.id;
  const { t } = useTranslation();
  const { showToast } = usePageActions();
  const labels = t.safecheck.disputes;
  const listLabels = labels.list;

  const accountType = getStoredAccountType();
  const isHospital = accountType === "hospital";
  const isReviewer = accountType === "auditor" || accountType === "admin";
  const homeHref = accountType ? getAccountTypeRoute(accountType) : "/dashboard/hospital";

  const [dispute, setDispute] = useState<DisputeApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState<DisputeStatusApi>("inProgress");
  const [savingComment, setSavingComment] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const load = useCallback(async () => {
    const numericId = Number.parseInt(id ?? "", 10);
    if (!id || Number.isNaN(numericId)) {
      setError("Identifiant de litige invalide.");
      setDispute(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    if (!getAuthToken()) {
      const mock = mockDispute(id, isHospital);
      setDispute(mock);
      if (mock) setStatus(mock.status);
      setError(mock ? null : "Litige introuvable (mode démo).");
      setLoading(false);
      return;
    }

    try {
      const row = isHospital
        ? await fetchHospitalDispute(numericId)
        : await fetchAuditorDispute(numericId);
      setDispute(row);
      setStatus(row.status);
    } catch (err) {
      setDispute(null);
      setError(
        err instanceof Error ? err.message : "Impossible de charger le litige.",
      );
    } finally {
      setLoading(false);
    }
  }, [id, isHospital]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAddComment = async () => {
    if (!dispute || !comment.trim()) {
      showToast({ message: "Saisissez un commentaire.", variant: "warning" });
      return;
    }

    if (!getAuthToken()) {
      showToast({ message: t.actions.saved, variant: "success" });
      setComment("");
      return;
    }

    setSavingComment(true);
    try {
      const updated = isHospital
        ? await commentHospitalDispute(dispute.id, comment.trim())
        : await commentAuditorDispute(dispute.id, comment.trim());
      setDispute(updated);
      setComment("");
      showToast({ message: "Commentaire ajouté.", variant: "success" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Ajout du commentaire impossible.";
      showToast({ message, variant: "warning" });
    } finally {
      setSavingComment(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!dispute) return;

    if (!getAuthToken()) {
      setDispute({ ...dispute, status });
      showToast({ message: t.actions.saved, variant: "success" });
      return;
    }

    setSavingStatus(true);
    try {
      const updated = isHospital
        ? await updateHospitalDisputeStatus(dispute.id, status)
        : await updateAuditorDisputeStatus(dispute.id, status);
      setDispute(updated);
      setStatus(updated.status);
      showToast({ message: "Statut mis à jour.", variant: "success" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Mise à jour du statut impossible.";
      showToast({ message, variant: "warning" });
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <Breadcrumbs
          homeHref={homeHref}
          items={[
            { label: labels.title, href: "/dashboard/disputes" },
            { label: dispute?.reference || `#${id}` },
          ]}
        />
        <Link
          to="/dashboard/disputes"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <ArrowLeft size={16} />
          Retour aux litiges
        </Link>
      </div>

      {loading ? (
        <div className="theme-card p-8 text-center text-sm text-muted">Chargement…</div>
      ) : error && !dispute ? (
        <div className="rounded-xl border border-danger/20 bg-danger-soft px-4 py-3">
          <p className="text-sm text-danger">{error}</p>
        </div>
      ) : dispute ? (
        <div className="space-y-6">
          <div className="theme-card p-5 md:p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h1 className="text-xl font-bold text-foreground">{dispute.reference}</h1>
              <span
                className={cn(
                  "inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide",
                  statusBadgeClass[dispute.status as DisputeStatus] ?? statusBadgeClass.inProgress,
                )}
              >
                {listLabels.statuses[dispute.status as DisputeStatus] ?? dispute.status}
              </span>
            </div>

            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
                  Hôpital
                </dt>
                <dd className="mt-1 text-sm text-foreground">{dispute.hospital || "—"}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
                  Entreprise
                </dt>
                <dd className="mt-1 text-sm text-foreground">{dispute.company || "—"}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
                  Facture
                </dt>
                <dd className="mt-1 text-sm text-foreground">{dispute.invoice || "—"}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
                  Montant
                </dt>
                <dd className="mt-1 text-sm font-medium text-foreground">
                  {formatAmount(Number(dispute.amount ?? 0))}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
                  Date
                </dt>
                <dd className="mt-1 text-sm text-foreground">
                  {dispute.submitted_date || dispute.createdAt || "—"}
                </dd>
              </div>
            </dl>

            <div className="mt-5 border-t border-border pt-5">
              <h2 className="mb-2 text-sm font-semibold text-foreground">Description</h2>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                {dispute.description || "Aucune description."}
              </p>
            </div>
          </div>

          {(isReviewer || isHospital) && (
            <div className="theme-card p-5 md:p-6">
              <h2 className="mb-4 text-sm font-semibold text-foreground">Mettre à jour le statut</h2>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <FormField label="Statut" className="flex-1">
                  <select
                    className={selectClass}
                    value={status}
                    onChange={(event) => setStatus(event.target.value as DisputeStatusApi)}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <button
                  type="button"
                  disabled={savingStatus}
                  onClick={() => void handleUpdateStatus()}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
                >
                  <Save size={16} />
                  {savingStatus ? "Enregistrement…" : "Mettre à jour"}
                </button>
              </div>
            </div>
          )}

          <div className="theme-card p-5 md:p-6">
            <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground">
              <MessageSquare size={16} className="text-primary" />
              Commentaires ({dispute.comments?.length ?? 0})
            </div>

            <div className="mb-5 space-y-3">
              {(dispute.comments ?? []).length === 0 ? (
                <p className="text-sm text-muted">Aucun commentaire pour le moment.</p>
              ) : (
                (dispute.comments ?? []).map((item) => (
                  <div
                    key={item.id}
                    className="rounded-xl border border-border bg-card-muted/30 px-4 py-3"
                  >
                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-foreground">
                        {item.author || `Utilisateur #${item.user_id}`}
                      </span>
                      <span className="text-xs text-muted">
                        {item.created_at
                          ? new Date(item.created_at).toLocaleString("fr-FR")
                          : ""}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-foreground">{item.comment}</p>
                  </div>
                ))
              )}
            </div>

            <FormField label="Ajouter un commentaire">
              <textarea
                className={`${textareaClass} min-h-[100px]`}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Votre commentaire…"
              />
            </FormField>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                disabled={savingComment}
                onClick={() => void handleAddComment()}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
              >
                <MessageSquare size={16} />
                {savingComment ? "Envoi…" : "Publier"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
